import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantRoles } from '../../hooks/useTenants';
import { TenantRole } from '../../types/auth';
import {
  XMarkIcon,
  EnvelopeIcon,
  UserPlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUserData) => void;
}

interface InviteUserData {
  email: string;
  name: string;
  role: string;
  sendWelcomeEmail: boolean;
  customMessage?: string;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ isOpen, onClose, onInvite }) => {
  const { currentTenant } = useAuth();
  // Roles are sourced from the dedicated TenantRole endpoint, not embedded on
  // the canonical Tenant.
  const { roles: tenantRoles } = useTenantRoles(currentTenant?.id || '');
  const [formData, setFormData] = useState<InviteUserData>({
    email: '',
    name: '',
    role: tenantRoles[0]?.name || '',
    sendWelcomeEmail: true,
    customMessage: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof InviteUserData, string>>>({});

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InviteUserData, string>> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onInvite(formData);
      // Reset form
      setFormData({
        email: '',
        name: '',
        role: tenantRoles[0]?.name || '',
        sendWelcomeEmail: true,
        customMessage: '',
      });
      setErrors({});
    }
  };

  const handleChange = (field: keyof InviteUserData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center">
            <UserPlusIcon className="h-6 w-6 text-primary mr-3" />
            <h2 className="text-lg font-semibold text-foreground">Invite User</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-accent transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.email ? 'border-red-500' : 'border-border'
                }`}
                placeholder="user@example.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-3 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.name ? 'border-red-500' : 'border-border'
              }`}
              placeholder="John Doe"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className={`w-full px-3 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors.role ? 'border-red-500' : 'border-border'
              }`}
            >
              <option value="">Select a role</option>
              {tenantRoles.map((role) => (
                <option key={role.id} value={role.name}>
                  {role.name}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-500">{errors.role}</p>
            )}
            {formData.role && (
              <div className="mt-2 p-3 bg-accent/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  {tenantRoles.find((r) => r.name === formData.role)?.description || 'No description available'}
                </p>
              </div>
            )}
          </div>

          {/* Send Welcome Email */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="sendWelcomeEmail"
              checked={formData.sendWelcomeEmail}
              onChange={(e) => handleChange('sendWelcomeEmail', e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="sendWelcomeEmail" className="text-sm text-foreground cursor-pointer">
              Send welcome email with login instructions
            </label>
          </div>

          {/* Custom Message */}
          {formData.sendWelcomeEmail && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Custom Message (Optional)
              </label>
              <textarea
                value={formData.customMessage}
                onChange={(e) => handleChange('customMessage', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add a personal message to the invitation email..."
              />
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  The user will receive an email invitation to join {currentTenant?.name}. They'll need to create a password when they first log in.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-accent text-foreground rounded-lg hover:bg-accent/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;